import React, { useCallback, useEffect, useRef, useState } from "react";
import "./ViewRoutine.scss";
import { BASE_URL } from "../../../utils/config";
import { adminRequest } from "../../../utils/requestMethods";
import { useNavigate } from "react-router-dom";
import CustomForm from "../../../components/CustomForm/CustomForm";

const ViewRoutine = () => {
  const [formData, setFormData] = useState({
    course: "",
    semester: "",
    section: "",
    enrollmentSetupId: "",
    routinePeriodId: "",
  });
  const [routineData, setRoutineData] = useState(null);
  const [timeSlots, setTimeSlots] = useState([]);
  const [course, setCourse] = useState([]);
  const [semester, setSemester] = useState([]);
  const [sections, setSections] = useState([]);
  const [academicYears, setAcademicYears] = useState([]);
  const [routinePeriod, setRoutinePeriod] = useState([]);
  const printRef = useRef();
  const navigate = useNavigate();

  const handleChange = (e) => {
    const updatedFormData = { ...formData, [e.target.name]: e.target.value };
    setFormData(updatedFormData);
  };

  const handlePrint = () => {
    const content = printRef.current;
    const printWindow = window.open("", "", "width=1200,height=800");
    printWindow.document.write(`
      <html>
        <head>
          <title>Print Routine</title>
          <style>
            @page { size: A4 landscape; margin: 10mm; }
            body { font-family: Arial, sans-serif; }
            table { width: 100%; border-collapse: collapse; }
            th, td { border: 1px solid #000; padding: 8px; text-align: center; }
            .routine-meta-program { font-size: 18px; font-weight: bold; margin-bottom: 4px; }
            .routine-meta-semester { font-size: 14px; margin-bottom: 10px; }
            .routine-entry { font-size: 14px;}
            .time-slot { font-size: 14px;}
          </style>
        </head>
        <body>
          ${content.innerHTML}
        </body>
      </html>
    `);
    printWindow.document.close();
    printWindow.focus();
    printWindow.print();
    printWindow.close();
  };

  const fetchData = useCallback(async () => {
    if (!formData.section) return;
    try {
      const response = await adminRequest.post(
        `${BASE_URL}/routine/list/table`,
        {
          program: formData.course,
          semester: formData.semester,
          section: formData.section,
          enrollmentSetupId: formData.enrollmentSetupId,
          routinePeriodId: formData.routinePeriodId,
        },
      );
      setRoutineData(response.data.data);

      const allTimeSlots = new Set();
      Object.values(response.data.data.data).forEach((day) => {
        Object.keys(day).forEach((slot) => allTimeSlots.add(slot));
      });
      setTimeSlots([...allTimeSlots].sort());
    } catch (error) {
      console.error("Error fetching routine:", error);
    }
  }, [formData]);

  useEffect(() => {
    fetchData();
  }, [formData]);

  useEffect(() => {
    const fetchProgram = async () => {
      try {
        const response = await adminRequest.get(
          `${BASE_URL}/course/getNameAndId`,
        );
        setCourse(response.data.data);
      } catch (error) {
        console.log(error);
      }
    };
    fetchProgram();
  }, []);

  useEffect(() => {
    if (formData.course) {
      const fetchSemester = async () => {
        try {
          const response = await adminRequest.post(
            `${BASE_URL}/course/getRunningSemestersById`,
            {
              id: formData.course,
            },
          );
          setSemester(response.data.data);
        } catch (error) {
          console.log(error);
        }
      };
      fetchSemester();
    }
  }, [formData.course]);

  useEffect(() => {
    if (formData.semester) {
      const fetchSection = async () => {
        try {
          const response = await adminRequest.post(
            `${BASE_URL}/section/list/by/curriculum`,
            {
              curriculumId: formData.semester,
            },
          );
          setSections(response.data.data);
        } catch (error) {
          console.log(error);
        }
      };
      fetchSection();
    }
  }, [formData.semester]);

  useEffect(() => {
    const fetchEnrollment = async () => {
      try {
        const response = await adminRequest.get(
          `${BASE_URL}/enrollmentSetup/list`,
        );
        setAcademicYears(response.data.data);
      } catch (error) {
        console.log(error);
      }
    };
    fetchEnrollment();
  }, []);

  useEffect(() => {
    if (formData.enrollmentSetupId) {
      const fetchRoutinePeriod = async () => {
        try {
          const response = await adminRequest.post(
            `${BASE_URL}/routinePeriod/list/enrollmentSetupId`,
            {
              enrollmentSetupId: formData.enrollmentSetupId,
            },
          );
          setRoutinePeriod(response.data.data);
        } catch (error) {
          console.log(error);
        }
      };
      fetchRoutinePeriod();
    }
  }, [formData.enrollmentSetupId]);

  const days = [
    "SUNDAY",
    "MONDAY",
    "TUESDAY",
    "WEDNESDAY",
    "THURSDAY",
    "FRIDAY",
  ];

  const filterFields = [
    {
      name: "enrollmentSetupId",
      label: "Enrollment",
      type: "select",
      value: formData.enrollmentSetupId,
      onChange: handleChange,
      options: [
        { label: "Select Enrollment", value: "" },
        ...academicYears.map((year) => ({
          label: year.enrollmentBatch,
          value: year.id,
        })),
      ],
    },
    {
      name: "routinePeriodId",
      label: "Routine Period",
      type: "select",
      value: formData.routinePeriodId,
      onChange: handleChange,
      options: [
        { label: "Select Routine Period", value: "" },
        ...routinePeriod.map((rp) => ({
          label: `${rp.effectiveFrom} to ${rp.effectiveTo}`,
          value: rp.id,
        })),
      ],
    },
    {
      name: "course",
      label: "Course",
      type: "select",
      value: formData.course,
      onChange: handleChange,
      options: [
        { label: "Select Course", value: "" },
        ...course.map((c) => ({ label: c.short_name, value: c.id })),
      ],
    },
    {
      name: "semester",
      label: "Semester",
      type: "select",
      value: formData.semester,
      onChange: handleChange,
      options: [
        { label: "Select Semester", value: "" },
        ...semester.map((s) => ({ label: s.semester, value: s.id })),
      ],
    },
    {
      name: "section",
      label: "Section",
      type: "select",
      value: formData.section,
      onChange: handleChange,
      options: [
        { label: "Select Section", value: "" },
        ...sections.map((s) => ({ label: s.Section.name, value: s.id })),
      ],
    },
  ];

  return (
    <div className="view-routine-container full-page">
      <div className="routine-header">
        <CustomForm
          fields={filterFields}
          flexDirection="row"
          header="View Routine"
          showCreateButton={false}
          showCancelButton={false}
          showDefaultHeader={true}
          showCloseButton={false}
        />
        {routineData && (
          <button className="print-button" onClick={handlePrint}>
            Print Routine
          </button>
        )}
      </div>

      {routineData && formData.section ? (
        <div className="routine-table-wrapper" ref={printRef}>
          <table className="routine-table bordered">
            <thead>
              <tr>
                <th colSpan={days.length + 1} className="routine-meta">
                  <div className="routine-meta-program">
                    Kantipur College of Management and Information Technology
                  </div>
                  <div className="routine-meta-program">
                    {routineData.program}
                  </div>
                  <div className="routine-meta-semester">
                    {`${routineData.semester} (${routineData.section})`}
                  </div>
                </th>
              </tr>
              <tr>
                <th>Time</th>
                {days.map((day) => (
                  <th key={day}>
                    {day.charAt(0) + day.slice(1).toLowerCase()}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {timeSlots.map((time) => (
                <tr key={time}>
                  <td className="time-slot">{time}</td>
                  {days.map((day) => (
                    <td key={day}>
                      {routineData.data[day]?.[time] ? (
                        <div className="routine-entry">
                          <div className="subject">
                            {routineData.data[day][time].subject}
                          </div>
                          <div className="faculty">
                            {`(${routineData.data[day][time].faculty})`}
                          </div>
                        </div>
                      ) : (
                        <div className="routine-entry empty">-</div>
                      )}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : null}
    </div>
  );
};

export default ViewRoutine;
